import { useEffect, useRef } from "react";
import { useFormikContext } from "formik";
import { useBlocker, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { getUserTypeName } from "../../../admin/navigation/adminNavigation";
import { CUSTOMER_PANEL_HOME } from "../../customer/customerNavigation";
import { PARTNER_PANEL_HOME } from "../../partner/partnerNavigation";

/**
 * Mirrors Formik values into the wizard's shared state on every change, so Back, the stepper
 * and the review step always see the latest data without needing a validated submit.
 */
export const FormSync = ({ onChange }) => {
  const { values, initialValues } = useFormikContext();
  // Only values that differ from the last synced snapshot are written, so merely opening a step
  // never marks the form as modified.
  const lastSynced = useRef(JSON.stringify(initialValues));
  useEffect(() => {
    const snapshot = JSON.stringify(values);
    if (snapshot !== lastSynced.current) {
      lastSynced.current = snapshot;
      onChange(values);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [values]);
  return null;
};

/** After a failed submit, scrolls to and focuses the first invalid control. */
export const FocusFirstError = () => {
  const { submitCount, isValid, isSubmitting } = useFormikContext();
  const lastCount = useRef(0);
  useEffect(() => {
    if (submitCount > lastCount.current && !isSubmitting && !isValid) {
      lastCount.current = submitCount;
      const el = document.querySelector('.fk-card [aria-invalid="true"]');
      if (el) {
        el.scrollIntoView({ behavior: "smooth", block: "center" });
        const focusable = el.matches("button, input, select, textarea") ? el : el.querySelector("button, input");
        if (focusable) focusable.focus({ preventScroll: true });
      }
    }
  }, [submitCount, isValid, isSubmitting]);
  return null;
};

// Set synchronously right after a successful save. A React state flag ("saved") can be applied after the
// follow-up navigation, which made the guard ask to discard changes that were just saved.
let leaveAllowedUntil = 0;
export const allowLeaveOnce = () => {
  leaveAllowedUntil = Date.now() + 3000;
};

/** Warns before leaving the page (reload/close and in-app navigation) while the form is dirty. */
export const useUnsavedChangesGuard = (isDirty) => {
  useEffect(() => {
    if (!isDirty) return undefined;
    const handler = (e) => {
      e.preventDefault();
      e.returnValue = "";
    };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [isDirty]);

  const blocker = useBlocker(({ currentLocation, nextLocation }) =>
    isDirty && Date.now() > leaveAllowedUntil && currentLocation.pathname !== nextLocation.pathname
  );

  useEffect(() => {
    if (blocker.state === "blocked") {
      if (window.confirm("You have unsaved changes. Leave this page and discard them?")) {
        blocker.proceed();
      } else {
        blocker.reset();
      }
    }
  }, [blocker]);
};

/**
 * Leaves a wizard from its first step: back to the page the user came from, or to their dashboard
 * home when the form was opened directly. Goes through the router, so the unsaved-changes
 * warning applies.
 */
export const useExitWizard = (fallbackPath) => {
  const navigate = useNavigate();
  const authState = useSelector((state) => state.auth);
  const role = getUserTypeName(authState);
  const fallback = fallbackPath || (role === "CUSTOMER" ? CUSTOMER_PANEL_HOME : role === "PARTNER" ? PARTNER_PANEL_HOME : "/property");
  return () => {
    if ((window.history.state?.idx ?? 0) > 0) navigate(-1);
    else navigate(fallback, { replace: true });
  };
};
